import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  HStack,
  ListItem,
  Select,
  Spinner,
  Text,
  UnorderedList,
  VStack,
  Badge,
} from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Lock } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { FESchedule, ParentTicket, ParentTicketStatus } from '@/types/parentTicket'
import { resolveSupportTypeLabel } from '@/hooks/useSupportTypeOptions'
import { apiClient } from '@/lib/api/client'
import { sendCsatSurvey } from '@/lib/services/csatService'
import {
  childTicketService,
  type CloseValidationBlockingTask,
} from '@/lib/services/childTicketService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  assignmentMethodBadgeColor,
  resolveAssignmentMethodLabel,
} from '@/lib/utils/assignmentMethod'
import {
  formatFeScheduleWhen,
  parentLevelFeSchedules,
} from '@/components/forms/parentTicket/feDispatchUtils'
import OverviewSectionCard from './OverviewSectionCard'
import PropertyRow from './PropertyRow'

const CSAT_ELIGIBLE_STATUSES = ['closed', 'resolved_incomplete']

/** Parent statuses that should trigger open-task close validation. */
const CLOSE_FAMILY_STATUSES = new Set(['closed', 'resolved_incomplete', 'cancelled'])

export function isCloseFamilyStatus(status: string): boolean {
  return CLOSE_FAMILY_STATUSES.has(
    String(status)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
  )
}

type Option = { label: string; value: string }

type Props = {
  ticketId: string
  ticket: ParentTicket
  statusOptions: Option[]
  supportTypeLabels: Record<string, string>
  supportType?: string | null
  source?: string | null
  storeCode?: string | null
  purchaseOrderDisplay?: string | null
  feSchedules?: FESchedule[]
  totalTicketTime?: string | null
  locationDetails?: {
    region?: string | null
    areaCluster?: string | null
    province?: string | null
    town?: string | null
  } | null
  ticketNumber?: string | null
  billable?: boolean | null
  poTrigger?: boolean | null
  onStatusSaved: (status: ParentTicketStatus) => void
  onRefreshActivity: () => void | Promise<void>
  onCsatSent?: () => void | Promise<void>
}

export default function TicketPropertiesPanel({
  ticketId,
  ticket,
  statusOptions,
  supportTypeLabels,
  supportType,
  source,
  storeCode,
  purchaseOrderDisplay,
  feSchedules = [],
  totalTicketTime,
  locationDetails,
  ticketNumber,
  billable,
  poTrigger,
  onStatusSaved,
  onRefreshActivity,
  onCsatSent,
}: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [statusValue, setStatusValue] = useState<string>(ticket.status)
  const [saving, setSaving] = useState(false)
  const [sendingCsat, setSendingCsat] = useState(false)
  const [pendingCloseStatus, setPendingCloseStatus] = useState<string | null>(null)
  const [blockingTasks, setBlockingTasks] = useState<CloseValidationBlockingTask[]>([])
  const [showBlockingDetails, setShowBlockingDetails] = useState(false)
  const csatCompleted = Boolean(ticket.csatRespondedAt)
  const csatEligible = CSAT_ELIGIBLE_STATUSES.includes(String(ticket.status).toLowerCase())

  const activityScheduleLabel = useMemo(() => {
    const parentSchedules = parentLevelFeSchedules(feSchedules)
    const primarySchedule =
      parentSchedules.find((s) => s.role === 'primary') ?? parentSchedules[0] ?? null
    return primarySchedule ? formatFeScheduleWhen(primarySchedule) : null
  }, [feSchedules])

  useEffect(() => {
    setStatusValue(ticket.status)
  }, [ticket.status])

  const clearCloseWarning = useCallback(() => {
    setPendingCloseStatus(null)
    setBlockingTasks([])
    setShowBlockingDetails(false)
  }, [])

  const applyStatusChange = useCallback(
    async (next: string, prev: string) => {
      setSaving(true)
      try {
        await apiClient.patch(`/tickets/${ticketId}`, { status: next })
        clearCloseWarning()
        onStatusSaved(next as ParentTicketStatus)
        showSuccess('Status updated')
        await onRefreshActivity()
      } catch (err) {
        setStatusValue(prev)
        showError(err as Error)
      } finally {
        setSaving(false)
      }
    },
    [ticketId, onStatusSaved, onRefreshActivity, showSuccess, showError, clearCloseWarning]
  )

  const handleStatusChange = useCallback(
    async (next: string) => {
      if (next === statusValue) return
      const prev = statusValue
      clearCloseWarning()

      if (isCloseFamilyStatus(next)) {
        setStatusValue(next)
        setSaving(true)
        try {
          const validation = await childTicketService.closeValidation(ticketId)
          if (!validation.can_close && validation.blocking_tasks.length > 0) {
            setPendingCloseStatus(next)
            setBlockingTasks(validation.blocking_tasks)
            setShowBlockingDetails(false)
            setSaving(false)
            return
          }
        } catch (err) {
          setStatusValue(prev)
          showError(err as Error)
          setSaving(false)
          return
        }
        await applyStatusChange(next, prev)
        return
      }

      setStatusValue(next)
      await applyStatusChange(next, prev)
    },
    [statusValue, ticketId, applyStatusChange, clearCloseWarning, showError]
  )

  const handleCloseAnyway = useCallback(async () => {
    if (!pendingCloseStatus) return
    const prev = ticket.status
    await applyStatusChange(pendingCloseStatus, prev)
  }, [pendingCloseStatus, ticket.status, applyStatusChange])

  const handleCancelClose = useCallback(() => {
    setStatusValue(ticket.status)
    clearCloseWarning()
  }, [ticket.status, clearCloseWarning])

  const handleSendCsat = useCallback(async () => {
    setSendingCsat(true)
    try {
      const result = await sendCsatSurvey(ticketId)
      showSuccess(
        result.resent ? 'CSAT resent' : 'CSAT sent',
        `Survey email sent to ${result.recipient_email}`,
      )
      await onCsatSent?.()
      await onRefreshActivity()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSendingCsat(false)
    }
  }, [ticketId, onCsatSent, onRefreshActivity, showSuccess, showError])

  return (
    <OverviewSectionCard title="Properties">
      <VStack align="stretch" spacing={3}>
        {ticketNumber ? <PropertyRow label="Ticket number" value={ticketNumber} /> : null}

        <FormControl>
          <PropertyRow
            label="Status"
            children={
              <HStack justify="flex-end" spacing={2}>
                {saving ? <Spinner size="xs" color={colors.primary} /> : null}
                <Select
                  size="sm"
                  value={statusValue}
                  onChange={(e) => void handleStatusChange(e.target.value)}
                  isDisabled={saving || Boolean(pendingCloseStatus)}
                  maxW="160px"
                  bg="white"
                  borderColor={colors.inputBorder ?? 'gray.200'}
                  fontSize="sm"
                  fontWeight="600"
                  data-testid="ticket-status-select"
                >
                  {statusOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </HStack>
            }
          />
        </FormControl>

        {pendingCloseStatus && blockingTasks.length > 0 ? (
          <Alert
            status="warning"
            variant="left-accent"
            borderRadius="md"
            alignItems="flex-start"
            data-testid="close-validation-banner"
          >
            <AlertIcon mt={1} />
            <Box flex="1">
              <AlertTitle fontSize="sm">
                {blockingTasks.length} task{blockingTasks.length === 1 ? '' : 's'} are still open.
                Closing this ticket will not close them.
              </AlertTitle>
              <AlertDescription fontSize="sm" mt={1}>
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="orange"
                  rightIcon={
                    showBlockingDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  }
                  onClick={() => setShowBlockingDetails((v) => !v)}
                  mb={showBlockingDetails ? 2 : 0}
                >
                  {showBlockingDetails ? 'Hide open tasks' : 'Show open tasks'}
                </Button>
                <Collapse in={showBlockingDetails}>
                  <UnorderedList spacing={1} pl={1} mb={2}>
                    {blockingTasks.map((task) => (
                      <ListItem key={task.id} fontSize="xs">
                        {[task.task_number, task.title].filter(Boolean).join(' — ') ||
                          `Task #${task.id}`}
                        {task.status ? ` (${task.status})` : ''}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </Collapse>
                <HStack spacing={2} mt={2}>
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={() => void handleCloseAnyway()}
                    isLoading={saving}
                    data-testid="close-ticket-anyway"
                  >
                    Close ticket anyway
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelClose}
                    isDisabled={saving}
                    data-testid="cancel-close-ticket"
                  >
                    Cancel
                  </Button>
                </HStack>
              </AlertDescription>
            </Box>
          </Alert>
        ) : null}

        <Divider borderColor={colors.borderLight} />

        <PropertyRow
          label="Agent"
          value={
            ticket.agentName ? (
              <HStack spacing={2} flexWrap="wrap">
                <Text as="span">{ticket.agentName}</Text>
                {resolveAssignmentMethodLabel(ticket.assignmentMethod) ? (
                  <Badge
                    colorScheme={assignmentMethodBadgeColor(ticket.assignmentMethod)}
                    fontSize="0.65rem"
                    textTransform="none"
                  >
                    {resolveAssignmentMethodLabel(ticket.assignmentMethod)}
                  </Badge>
                ) : null}
              </HStack>
            ) : (
              '—'
            )
          }
        />
        <PropertyRow
          label="Support Tier"
          value={resolveSupportTypeLabel(supportType, supportTypeLabels)}
        />
        <PropertyRow label="Source" value={source ?? '—'} />
        <PropertyRow label="Purchase order" value={purchaseOrderDisplay ?? '—'} />
        <PropertyRow label="Activity schedule" value={activityScheduleLabel ?? '—'} />
        <PropertyRow label="Total open duration" value={totalTicketTime ?? '—'} />
        {billable != null || poTrigger ? (
          <PropertyRow
            label="Billing"
            value={
              [
                billable === true ? 'Billable' : billable === false ? 'Non-billable' : null,
                poTrigger ? 'PO required' : null,
              ]
                .filter(Boolean)
                .join(' · ') || '—'
            }
          />
        ) : null}

        <Divider borderColor={colors.borderLight} />

        <PropertyRow
          label="CSAT score"
          value={
            csatCompleted && ticket.csatScore != null
              ? String(ticket.csatScore)
              : csatCompleted
                ? 'Completed'
                : '—'
          }
        />
        {!csatCompleted && csatEligible ? (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            alignSelf="flex-start"
            onClick={() => void handleSendCsat()}
            isLoading={sendingCsat}
            isDisabled={sendingCsat}
          >
            Send CSAT survey
          </Button>
        ) : !csatCompleted ? (
          <HStack spacing={1.5} py={0.5}>
            <Lock size={12} color={colors.textMuted} aria-hidden />
            <Text fontSize="xs" color={colors.textMuted}>
              Available once closed
            </Text>
          </HStack>
        ) : null}
      </VStack>
    </OverviewSectionCard>
  )
}
